// src/components/SimpleComponents/SimpleMultiDiscount.jsx
// *******************************************************************************
// SIMPLE MULTI DISCOUNT
// *******************************************************************************
// Hybrid input that accepts chained discount notation (e.g. "10+10+5") and
// toggles between chain-percentage display and currency (R$) display.
//
// Form value structure:
//   { chain: '10+10+5', totalPercentage: 23.05, value: <base * 0.2305> }
//
// Chain formula: total = (1 - ((1-d1/100) * (1-d2/100) * ... * (1-dn/100))) * 100
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Input, Select, Space } from 'antd';
import { useSimpleFormContext } from './SimpleFormProvider';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';

const { Option } = Select;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODE_CHAIN = 'chain';
const MODE_CURRENCY = 'currency';

const DEFAULT_CURRENCIES = [{ value: 'BRL', label: 'R$', text: 'Real Brasileiro' }];

const LOCALE_CONFIGS = {
  'pt-BR': { thousandSeparator: '.', decimalSeparator: ',' },
  'en-US': { thousandSeparator: ',', decimalSeparator: '.' },
  default: { thousandSeparator: ',', decimalSeparator: '.' },
};

// Chars allowed inside the chain input field
const CHAIN_ALLOWED_KEYS = new Set([
  'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
  'Home', 'End',
]);

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleMultiDiscount = ({
  name,
  style = {},
  subdocumentField,

  // Reference value used for chain% -> R$ conversion
  base = 0,

  // Initial display mode
  init = MODE_CHAIN,

  // Locale
  locale = 'pt-BR',

  // Currency suffix options
  currencies = DEFAULT_CURRENCIES,

  // Other props
  disabled,
  label,
  placeholder,
  onBlur,
  onChange,

  ...props
}) => {
  // ---------------------------------------------------------------------------
  // CONTEXTS
  // ---------------------------------------------------------------------------
  const { locked, disabledFields, form } = useSimpleFormContext();

  // ---------------------------------------------------------------------------
  // HOOKS
  // ---------------------------------------------------------------------------
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;
  const { customValidationStatus, customValidationMessage } = useCustomValidationStatus({
    ...props,
    ...elementProps,
  });

  // ---------------------------------------------------------------------------
  // LOCAL VARS I
  // ---------------------------------------------------------------------------
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);
  const localeConfig = LOCALE_CONFIGS[locale] || LOCALE_CONFIGS.default;

  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------
  const [displayValue, setDisplayValue] = useState('');
  const [currentMode, setCurrentMode] = useState(init);
  const [chainString, setChainString] = useState('');         // raw chain string e.g. "10+10+5"
  const [totalPercentage, setTotalPercentage] = useState(0);  // computed total %
  const [monetaryValue, setMonetaryValue] = useState(0);      // computed R$ value
  const [chainValid, setChainValid] = useState(null);         // null | true | false
  const isFocusedRef = useRef(false);
  const isInitializedRef = useRef(false);

  // Watch external form value changes (e.g. reset, load from server)
  const watchedValue = Form.useWatch(fullFieldName, form);

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------
  // Initialize or sync from form value
  useEffect(() => {
    if (isInitializedRef.current && isFocusedRef.current) return;

    if (watchedValue && typeof watchedValue === 'object') {
      const savedChain = watchedValue.chain ?? '';
      const savedTotal = watchedValue.totalPercentage ?? 0;
      const savedValue = watchedValue.value ?? 0;
      const savedMode = watchedValue.init ?? init;

      setChainString(savedChain);
      setTotalPercentage(savedTotal);
      setMonetaryValue(savedValue);
      setCurrentMode(savedMode);

      const { isValid } = validateChain(savedChain);
      setChainValid(savedChain === '' ? null : isValid);

      if (savedMode === MODE_CHAIN) {
        setDisplayValue(savedChain);
      } else {
        setDisplayValue(formatCurrency(savedValue, localeConfig));
      }

      isInitializedRef.current = true;
    } else if (!isInitializedRef.current) {
      setChainString('');
      setTotalPercentage(0);
      setMonetaryValue(0);
      setCurrentMode(init);
      setDisplayValue('');
      setChainValid(null);
      isInitializedRef.current = true;
    }
  }, [watchedValue, init, localeConfig]);

  // Recalculate monetary value when base changes
  useEffect(() => {
    if (base > 0 && totalPercentage > 0) {
      const newValue = roundTo((base * totalPercentage) / 100, 2);
      setMonetaryValue(newValue);
      pushFormValue(currentMode, chainString, totalPercentage, newValue);
    }
  }, [base]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------
  const pushFormValue = useCallback(
    (mode, chain, total, value) => {
      const formData = {
        init: mode,
        chain,
        totalPercentage: roundTo(total, 4),
        value: roundTo(value, 2),
      };
      form?.setFieldValue(fullFieldName, formData);
      onChange?.(formData);
    },
    [form, fullFieldName, onChange]
  );

  // Block characters that are not digits, comma, period or plus
  const handleKeyDown = useCallback(
    ev => {
      if (currentMode !== MODE_CHAIN) return;
      if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
      if (CHAIN_ALLOWED_KEYS.has(ev.key)) return;
      if (/^[0-9.,+]$/.test(ev.key)) return;
      ev.preventDefault();
    },
    [currentMode]
  );

  const handleInputChange = useCallback(
    ev => {
      const raw = ev?.target?.value ?? '';

      if (currentMode === MODE_CHAIN) {
        // Allow only valid chain chars
        if (!/^[0-9.,+]*$/.test(raw)) return;

        setDisplayValue(raw);
        setChainString(raw);

        if (raw === '') {
          setTotalPercentage(0);
          setMonetaryValue(0);
          setChainValid(null);
          pushFormValue(MODE_CHAIN, '', 0, 0);
          return;
        }

        const { isValid, total } = validateChain(raw);
        setChainValid(isValid);

        if (isValid) {
          const newValue = base > 0 ? roundTo((base * total) / 100, 2) : 0;
          setTotalPercentage(total);
          setMonetaryValue(newValue);
          pushFormValue(MODE_CHAIN, raw, total, newValue);
        } else {
          // Keep last valid total; do not push while chain is mid-edit
          setTotalPercentage(0);
          setMonetaryValue(0);
        }
      } else {
        // Currency mode: digits + locale decimal separator only
        const decSep = localeConfig.decimalSeparator;
        const regex = new RegExp(`^[0-9${escapeRegex(decSep)}]*$`);
        if (!regex.test(raw) && raw !== '') return;

        // Prevent multiple decimal separators
        if (decSep) {
          const sepCount = (raw.match(new RegExp(escapeRegex(decSep), 'g')) || []).length;
          if (sepCount > 1) return;
        }

        setDisplayValue(raw);

        const numericValue = parseLocaleNumber(raw, decSep);
        if (!isNaN(numericValue) && raw !== '') {
          const newTotal = base > 0 ? (numericValue / base) * 100 : 0;
          const flatChain = roundTo(newTotal, 4).toString();
          setMonetaryValue(numericValue);
          setTotalPercentage(newTotal);
          setChainString(flatChain);
          pushFormValue(MODE_CURRENCY, flatChain, newTotal, numericValue);
        } else if (raw === '') {
          setMonetaryValue(0);
          setTotalPercentage(0);
          setChainString('');
          pushFormValue(MODE_CURRENCY, '', 0, 0);
        }
      }
    },
    [currentMode, base, localeConfig, pushFormValue]
  );

  const handleBlur = useCallback(
    ev => {
      isFocusedRef.current = false;
      const raw = ev?.target?.value ?? '';

      if (currentMode === MODE_CHAIN) {
        if (raw === '') {
          setDisplayValue('');
          setChainValid(null);
          onBlur?.(ev);
          return;
        }

        // Trim trailing + and revalidate
        const normalized = raw.replace(/\++$/, '');
        const { isValid, total } = validateChain(normalized);
        setChainString(normalized);
        setChainValid(isValid);
        setDisplayValue(normalized);

        if (isValid) {
          const newValue = base > 0 ? roundTo((base * total) / 100, 2) : 0;
          setTotalPercentage(total);
          setMonetaryValue(newValue);
          pushFormValue(MODE_CHAIN, normalized, total, newValue);
        }
      } else {
        // Currency mode: format on blur
        if (raw === '') {
          setDisplayValue('');
          onBlur?.(ev);
          return;
        }

        const decSep = localeConfig.decimalSeparator;
        let numericValue = parseLocaleNumber(raw, decSep);
        if (isNaN(numericValue)) {
          setDisplayValue('');
          pushFormValue(MODE_CURRENCY, '', 0, 0);
          onBlur?.(ev);
          return;
        }

        const maxValue = base || 999999999.99;
        if (numericValue < 0) numericValue = 0;
        if (numericValue > maxValue) numericValue = maxValue;
        numericValue = roundTo(numericValue, 2);

        const newTotal = base > 0 ? (numericValue / base) * 100 : 0;
        const flatChain = roundTo(newTotal, 4).toString();

        setMonetaryValue(numericValue);
        setTotalPercentage(newTotal);
        setChainString(flatChain);
        setDisplayValue(formatCurrency(numericValue, localeConfig));
        pushFormValue(MODE_CURRENCY, flatChain, newTotal, numericValue);
      }

      onBlur?.(ev);
    },
    [currentMode, base, localeConfig, pushFormValue, onBlur]
  );

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;

    if (currentMode === MODE_CURRENCY) {
      // Strip thousand separators for easier editing
      const decSep = localeConfig.decimalSeparator;
      const thouSep = localeConfig.thousandSeparator;
      const cleaned = displayValue.replace(new RegExp(escapeRegex(thouSep), 'g'), '');
      setDisplayValue(cleaned);
    }

    // Select all on focus
    setTimeout(() => {
      elementRef?.current?.select?.();
    }, 0);
  }, [currentMode, displayValue, localeConfig, elementRef]);

  const handleModeChange = useCallback(
    newMode => {
      setCurrentMode(newMode);

      if (newMode === MODE_CHAIN) {
        // Switch to chain display
        setDisplayValue(chainString);
      } else {
        // Switch to currency display — compute from current totalPercentage
        const currValue = base > 0 ? roundTo((base * totalPercentage) / 100, 2) : monetaryValue;
        setMonetaryValue(currValue);
        setDisplayValue(formatCurrency(currValue, localeConfig));
        pushFormValue(MODE_CURRENCY, chainString, totalPercentage, currValue);
      }
    },
    [chainString, totalPercentage, monetaryValue, base, localeConfig, pushFormValue]
  );

  // ---------------------------------------------------------------------------
  // LOCAL VARS II
  // ---------------------------------------------------------------------------

  // Determine validation status to pass to SimpleFormItem
  // In chain mode: override with chain-specific validation
  // In currency mode: use the standard customValidationStatus from hook
  const effectiveValidationStatus = useMemo(() => {
    if (currentMode === MODE_CHAIN) {
      if (chainValid === null) return customValidationStatus;
      return chainValid ? 'success' : 'error';
    }
    return customValidationStatus;
  }, [currentMode, chainValid, customValidationStatus]);

  const effectiveValidationMessage = useMemo(() => {
    if (currentMode === MODE_CHAIN && chainValid === false) {
      return 'Desconto em cadeia inválido';
    }
    return customValidationMessage;
  }, [currentMode, chainValid, customValidationMessage]);

  // Build toggle select (% vs R$)
  const suffixElement = useMemo(() => {
    const options = buildSuffixOptions(currencies);
    return (
      <Select
        disabled={isDisabled}
        value={currentMode}
        onChange={handleModeChange}
        style={{ width: 70, minWidth: 70 }}
        getPopupContainer={trigger => trigger.parentNode}
      >
        {options.map((opt, idx) => (
          <Option key={`${opt.value}-${idx}`} value={opt.value} title={opt.text}>
            {opt.label}
          </Option>
        ))}
      </Select>
    );
  }, [currencies, currentMode, isDisabled, handleModeChange]);

  // Resolve placeholder
  const resolvedPlaceholder = useMemo(() => {
    if (placeholder !== undefined) return placeholder;
    if (currentMode === MODE_CHAIN) return 'Ex: 10+5+2';
    return `0${localeConfig.decimalSeparator}00`;
  }, [placeholder, currentMode, localeConfig]);

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------
  return (
    <SimpleFormItem
      {...props}
      label={label}
      name={fullFieldName}
      elementProps={elementProps}
      customValidationStatus={effectiveValidationStatus}
      customValidationMessage={effectiveValidationMessage}
      hasFeedback={currentMode === MODE_CHAIN}
    >
      <Space.Compact style={{ width: '100%' }}>
        <Input
          ref={elementRef}
          id={flattenFieldName}
          style={{
            flex: 1,
            height: 40,
            textAlign: 'right',
            ...style,
          }}
          value={displayValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={handleFocus}
          disabled={isDisabled}
          placeholder={resolvedPlaceholder}
        />
        {suffixElement}
      </Space.Compact>
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleMultiDiscount;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Validates a chain discount string and computes the total discount percentage.
 * Returns { isValid: boolean, total: number, parts: number[] }
 */
function validateChain(chain) {
  if (!chain || chain.trim() === '') {
    return { isValid: false, total: 0, parts: [] };
  }

  const parts = chain.split('+');

  // Must have at least one part
  if (parts.length === 0) return { isValid: false, total: 0, parts: [] };

  const parsed = [];
  for (const part of parts) {
    const trimmed = part.trim();

    // Empty part means consecutive ++ or leading/trailing +
    if (trimmed === '') return { isValid: false, total: 0, parts: [] };

    // Normalize comma to dot for parsing
    const numeric = parseFloat(trimmed.replace(',', '.'));

    if (isNaN(numeric)) return { isValid: false, total: 0, parts: [] };
    if (numeric < 0 || numeric > 100) return { isValid: false, total: 0, parts: [] };

    parsed.push(numeric);
  }

  // Compute chained discount
  let remaining = 1;
  for (const d of parsed) {
    remaining *= 1 - d / 100;
  }
  const total = roundTo((1 - remaining) * 100, 4);

  return { isValid: true, total, parts: parsed };
}

/**
 * Builds the suffix options array for the toggle Select.
 */
function buildSuffixOptions(currencies) {
  const options = [{ value: MODE_CHAIN, label: '%', text: 'Desconto em Cadeia (%)' }];
  currencies.forEach(c => {
    options.push({ value: MODE_CURRENCY, label: c.label, text: c.text });
  });
  return options;
}

/**
 * Formats a number as a currency display string with locale separators.
 */
function formatCurrency(value, localeConfig) {
  if (value === null || value === undefined) return '';
  const numValue = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(numValue)) return '';

  const parts = numValue.toFixed(2).split('.');
  let intPart = parts[0];
  const decPart = parts[1] || '00';

  // Apply thousand separator
  if (localeConfig.thousandSeparator) {
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, localeConfig.thousandSeparator);
  }

  return `${intPart}${localeConfig.decimalSeparator}${decPart}`;
}

/**
 * Parses a locale-formatted number string into a float.
 */
function parseLocaleNumber(value, decimalSep) {
  if (!value || value === '') return NaN;
  const normalized = value.replace(decimalSep, '.');
  return parseFloat(normalized);
}

/**
 * Rounds a number to a given number of decimal places.
 */
function roundTo(value, decimals) {
  if (isNaN(value)) return 0;
  const multiplier = Math.pow(10, decimals);
  return Math.round(value * multiplier) / multiplier;
}

/**
 * Escapes special regex characters in a string.
 */
function escapeRegex(str) {
  if (!str) return '';
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
